import { useCallback, useEffect, useMemo, useRef } from 'react';
import Chart from 'chart.js/auto';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import sanitizeHtml from 'sanitize-html';
import { useTheme } from '@Sage/hoi-poi-ui';
import { useWeb5Context } from 'lib/Web5Context';
import { getNumberAsLocaleNumber, formatLargeNumber } from 'utils/numbers';
import { getVar } from 'utils/colors';
import { getEntityFromString } from 'utils/getEntityFromString';

import {
    CHART_REGEX,
    CHART_REGEX_GLOBAL,
    DETAIL_REGEX,
    DETAIL_REGEX_GLOBAL,
    TABLE_REGEX,
    TABLE_REGEX_GLOBAL,
    COPILOT_CHAT_STREAM_IDENTIFIERS,
    CHART_DEFAULTS,
} from './constants';

import {
    removeTags,
    removeMarkdown,
    processLiveMarkdown,
    getRegexMatch,
    getRegexExec,
    isColorDark,
    getDifference,
} from './utils';

import { createInfoBox, createTable } from './dom-utils';

/**
 * Custom React hook for building and processing chat answers with dynamic content.
 * Handles text processing, chart generation, table creation, and interactive elements
 * for a copilot chat interface.
 *
 * @param {Object} params - Hook parameters
 * @param {Object} params.customEventHandlers - Custom event handlers for user interactions
 * @param {Function} [params.customEventHandlers.redirectToDetail] - Handler for detail link clicks
 *
 * @returns {Object} Hook return object
 * @returns {Function} insertTextInDOM - Function to insert text content into DOM with live processing
 * @returns {Function} cleanChatTextForClipboard - Function to clean text for clipboard copying
 * @returns {Function} parseCopilotEmbeds - Function to parse copilot embeds
 *
 * @example
 * ```jsx
 * const { insertTextInDOM, cleanChatTextForClipboard, parseCopilotEmbeds } = useBuildAnswer({
 *   customEventHandlers: {
 *     redirectToDetail: (target) => {
 *       // Handle detail link clicks
 *       const detailId = target.getAttribute('data-fm_detail');
 *       navigate(`/detail/${detailId}`);
 *     }
 *   }
 * });
 * ```
 */
const useBuildAnswer = ({ customEventHandlers } = {}) => {
    const { colors } = useTheme();
    const { t } = useWeb5Context();
    const chartImagesRef = useRef({});
    const isFirstRender = useRef(true);

    // Clear chart images on mount
    useEffect(() => {
        if (isFirstRender.current) {
            chartImagesRef.current = {};
            isFirstRender.current = false;
        }
    }, []);

    // Memoized replacement functions
    const replacementFunctions = useMemo(
        () => ({
            /**
             * Replaces detail identifiers with HTML links or bold text
             * @param {boolean} avoidLink - Whether to avoid creating links and use bold text instead
             * @returns {Function} Replacement function
             */
            replaceForDetail:
                (avoidLink = false) =>
                (text) => {
                    const info = getRegexMatch(text, DETAIL_REGEX);
                    const [entityName, id, name] = info.split(',');

                    if (!entityName || !id || !name) return text;

                    return avoidLink
                        ? `<b>${name}</b>`
                        : `<a data-fm_detail="${entityName}-${id}">${name}</a>`;
                },

            /**
             * Replaces chart identifiers with chart container divs
             * @param {string} text - Text containing chart identifier
             * @returns {string} HTML with chart container
             */
            replaceForChart: (text) => {
                const id = getRegexMatch(text, CHART_REGEX);
                return id ? `<div data-fm_report="${id}"></div>` : text;
            },

            /**
             * Replaces table identifiers with table container divs
             * @param {string} text - Text containing table identifier
             * @returns {string} HTML with table container
             */
            replaceForTable: (text) => {
                const id = getRegexMatch(text, TABLE_REGEX);
                return id ? `<div data-fm_table="${id}"></div>` : text;
            },
        }),
        [],
    );

    /**
     * Builds and renders a Chart.js chart in the specified container
     * @param {string} id - Unique identifier for the chart
     * @param {HTMLElement} container - DOM container to render the chart
     * @param {Object} data - Chart.js configuration data
     */
    const buildChart = useCallback(
        (id, container, data) => {
            if (!data || !Object.keys(data)?.length) return;

            const chartConfig = {
                ...data,
                options: {
                    ...(data.options || {}),
                    devicePixelRatio: CHART_DEFAULTS.devicePixelRatio,
                    responsive: true,
                    layout: {
                        ...(data.options?.layout || {}),
                        padding: CHART_DEFAULTS.padding,
                    },
                    animation: {
                        onComplete: () => {
                            chartImagesRef.current[id] = newChart.toBase64Image();
                        },
                    },
                    plugins: {
                        customCanvasBackgroundColor: {
                            color: getVar(colors.actionMinor[50]),
                        },
                        tooltip: {
                            enabled: true,
                            callbacks: {
                                title: (tooltipItem) => tooltipItem.label,
                                label: (tooltipItem) => {
                                    let value = tooltipItem.dataset.data?.[tooltipItem.dataIndex];
                                    if (isNaN(value)) return '';
                                    value = getNumberAsLocaleNumber(value.toString());
                                    const label = tooltipItem?.dataset?.label || '';
                                    return label ? `${label}: ${value}` : value;
                                },
                            },
                            boxPadding: 4,
                        },
                        datalabels: {
                            color: (context) => {
                                const backgroundColors = context?.dataset?.backgroundColor || [];
                                const backgroundColor = backgroundColors?.[context.dataIndex];
                                const isChartPie = ['pie', 'doughnut'].includes(data.type);

                                if (!backgroundColor?.length || !isChartPie) {
                                    return getVar(colors.grey[700]);
                                }

                                const darks = backgroundColors.filter((current) => {
                                    const rgbArray = current
                                        .match(/\d+/g)
                                        ?.map((val) => parseInt(val, 10));
                                    return rgbArray ? isColorDark(rgbArray) : false;
                                });

                                return darks.length >= Math.ceil(backgroundColors.length / 2)
                                    ? getVar(colors.primary.white)
                                    : getVar(colors.grey[700]);
                            },
                            font: {
                                weight: ['pie', 'doughnut'].includes(data.type) ? 'bold' : 'normal',
                            },
                            anchor: 'center',
                            formatter: (value, context) => {
                                if (!value) return null;
                                const hasMultipleDatasets = data.data.datasets.length > 1;
                                return hasMultipleDatasets
                                    ? `${context.dataset.label}\n${formatLargeNumber(value)}`
                                    : formatLargeNumber(value);
                            },
                            textAlign: 'center',
                            display: 'auto',
                        },
                    },
                },
                plugins: [
                    ChartDataLabels,
                    {
                        id: 'customCanvasBackgroundColor',
                        beforeDraw: (chart, args, options) => {
                            const { ctx } = chart;
                            ctx.save();
                            ctx.globalCompositeOperation = 'destination-over';
                            ctx.fillStyle = options.color || getVar(colors.actionMinor[50]);
                            ctx.fillRect(0, 0, chart.width, chart.height);
                            ctx.restore();
                        },
                    },
                ],
            };

            const canvas = document.createElement('canvas');
            canvas.width = CHART_DEFAULTS.width;
            canvas.height = CHART_DEFAULTS.height;

            container.appendChild(canvas);
            const ctx = canvas.getContext('2d');
            const newChart = new Chart(ctx, chartConfig);
        },
        [colors],
    );

    /**
     * Creates an info box container for charts and tables
     * @param {Object} params - Info box parameters
     * @param {HTMLElement} params.container - Parent container
     * @param {boolean} params.isChart - Whether this is for a chart
     * @param {boolean} params.isTable - Whether this is for a table
     * @param {string} params.id - Unique identifier
     * @returns {HTMLElement} Created info box body element
     */
    const buildInfoBox = useCallback(
        ({ container, isChart, isTable, id }) => {
            return createInfoBox({ container, isChart, isTable, id, t });
        },
        [t],
    );

    /**
     * Creates a table element from table data
     * @param {Array} tableData - Array of table row objects
     * @returns {HTMLElement} Created table element
     */
    const createTableElement = useCallback(
        (tableData) => {
            return createTable(tableData, replacementFunctions.replaceForDetail);
        },
        [replacementFunctions],
    );

    /**
     * Processes live content to create detail links dynamically
     * @param {HTMLElement} textBox - Text container element
     */
    const processLiveRedirect = useCallback((textBox) => {
        if (!textBox) return;

        const htmlText = textBox.innerHTML;
        const { match, info } = getRegexExec(htmlText, DETAIL_REGEX);

        if (!match || !info) return;

        const infoArray = info.split(',');
        if (!infoArray.length) return;

        const [entity, id, name] = infoArray;
        if (!entity || !name || !id) return;

        // Remove identifier from the text
        const newText = htmlText.replace(match, '');
        textBox.innerHTML = newText;

        const link = document.createElement('a');
        link.innerText = name;
        link.setAttribute('data-fm_detail', `${entity}-${id}`);
        link.setAttribute('id', id);
        textBox.appendChild(link);
    }, []);

    /**
     * Processes live content to create charts dynamically
     * @param {HTMLElement} textBox - Text container element
     * @param {Object} data - Chart data object with chart configurations
     */
    const processLiveChart = useCallback(
        (textBox, data) => {
            if (!textBox) return;

            const htmlText = textBox.innerHTML;
            const { match, info } = getRegexExec(htmlText, CHART_REGEX);
            const id = info;

            if (!match || !id || !data?.[id]) return;

            const reportData = data[id]?.content;
            const newText = htmlText.replace(match, replacementFunctions.replaceForChart);
            textBox.innerHTML = newText;

            const container = document.querySelector(`[data-fm_report="${id}"]`);

            if (container && reportData && Object.keys(reportData)?.length) {
                const chartBody = buildInfoBox({ container, isChart: true, id });
                buildChart(id, chartBody, reportData);
            }
        },
        [replacementFunctions, buildInfoBox, buildChart],
    );

    /**
     * Processes live content to create tables dynamically
     * @param {HTMLElement} textBox - Text container element
     * @param {Object} data - Table data object with table configurations
     */
    const processLiveTable = useCallback(
        (textBox, data) => {
            if (!textBox) return;

            const htmlText = textBox.innerHTML;
            const { match, info } = getRegexExec(htmlText, TABLE_REGEX);
            const id = info;

            if (!match || !id || !data?.[id]) return;

            const tableData = data[id]?.content;
            const newText = htmlText.replace(match, replacementFunctions.replaceForTable);
            textBox.innerHTML = newText;

            const container = document.querySelector(`[data-fm_table="${id}"]`);

            if (container && tableData?.length) {
                const tableBody = buildInfoBox({ container, isTable: true, id });
                const table = createTableElement(tableData);
                if (table) tableBody.appendChild(table);
            }
        },
        [replacementFunctions, buildInfoBox, createTableElement],
    );

    /**
     * Main function to insert text content into DOM with live processing
     * @param {Object} params - Insertion parameters
     * @param {string} [params.text] - Text content to insert
     * @param {string} [params.progress] - Progress text to display
     * @param {HTMLElement} params.chatBox - Chat container element
     * @param {string} [params.prevTextRef] - Previous text reference for diff calculation
     * @param {Object} [params.reports] - Chart data object
     * @param {Object} [params.tables] - Table data object
     */
    const insertTextInDOM = useCallback(
        ({ text, progress, chatBox, prevTextRef, reports, tables }) => {
            if (!chatBox || (!text && !progress)) return;

            const textBox = chatBox.querySelector('.dana-chat-comment-text__inner');
            const prevText = prevTextRef || '';

            if (progress) {
                textBox.classList.toggle('dana-chat-comment-text__progress', true);
                textBox.innerHTML = sanitizeHtml(progress);
            } else if (text) {
                textBox.classList.toggle('dana-chat-comment-text__progress', false);

                if (!prevText) textBox.innerHTML = '';

                let textPortion = getDifference(prevText, text);
                textPortion = processLiveMarkdown(textPortion);
                textBox.insertAdjacentHTML('beforeend', sanitizeHtml(textPortion));

                processLiveRedirect(textBox);
                processLiveTable(textBox, tables);
                processLiveChart(textBox, reports);
            }
        },
        [processLiveRedirect, processLiveTable, processLiveChart],
    );

    /**
     * Generates a markdown table from data array
     * @param {Array} data - Array of objects representing table rows
     * @returns {string} Formatted markdown table string
     */
    const generateMarkdownTable = useCallback((data) => {
        if (!data?.length) return '';

        const headers = Object.keys(data[0]);
        const columnWidths = headers.reduce((acc, header) => {
            acc[header] = Math.max(
                header.length,
                ...data.map((row) => row[header]?.toString().length || 0),
            );
            return acc;
        }, {});

        let markdownTable = '|';
        headers.forEach((header) => {
            markdownTable += ` ${header.padEnd(columnWidths[header])} |`;
        });
        markdownTable += '\n|';

        headers.forEach((header) => {
            markdownTable += ` ${'-'.repeat(columnWidths[header])} |`;
        });
        markdownTable += '\n';

        data.forEach((row) => {
            markdownTable += '|';
            headers.forEach((header) => {
                let cleanValue = '';
                const value = row[header];
                if (value && typeof value === 'string') {
                    cleanValue = value.replace(DETAIL_REGEX_GLOBAL, (text) => {
                        const info = getRegexMatch(text, DETAIL_REGEX);
                        const [, , name] = info.split(',');
                        return name || '';
                    });
                } else {
                    cleanValue = value || '';
                }
                markdownTable += ` ${cleanValue.toString().padEnd(columnWidths[header])} |`;
            });
            markdownTable += '\n';
        });

        return `\n${markdownTable}`;
    }, []);

    /**
     * Cleans chat text for clipboard copying by removing HTML tags and converting
     * special elements (charts, tables) to markdown format
     * @param {Object} params - Cleaning parameters
     * @param {string} params.value - Text content to clean
     * @param {Object} [params.tables] - Table data object
     * @param {Object} [params.reports] - Chart data object
     * @returns {string} Cleaned text suitable for clipboard
     */
    const cleanChatTextForClipboard = useCallback(
        ({ value, tables, reports }) => {
            let finalText = value;
            finalText = removeTags(finalText);
            finalText = removeMarkdown(finalText);

            // Replace detail links with plain text
            finalText = finalText.replace(DETAIL_REGEX_GLOBAL, (text) => {
                const info = getRegexMatch(text, DETAIL_REGEX);
                const [, , name] = info.split(',');
                return name ? `"${name}"` : text;
            });

            // Replace tables with markdown
            if (tables) {
                finalText = finalText.replace(TABLE_REGEX_GLOBAL, (text) => {
                    const { info } = getRegexExec(text, TABLE_REGEX);
                    const id = info;

                    if (!id || !tables[id]?.length) return '';

                    return generateMarkdownTable(tables[id]);
                });
            }

            // Remove charts placeholders
            finalText = finalText.replace(CHART_REGEX_GLOBAL, '');

            return finalText;
        },
        [generateMarkdownTable],
    );

    const parseDetailLinks = useCallback((text) => {
        const { match, info } = getRegexExec(text, DETAIL_REGEX);
        if (!match || !info) return text;
        const [entityName, id, name, type] = info.split(',').map((item) => item.trim());

        // Validate required fields to avoid generating malformed links
        if (!entityName || !id || !name) return text;

        const route = getEntityFromString(entityName)?.route;
        if (!route) return text;

        const encodedId = encodeURIComponent(id);
        const encodedType = type ? encodeURIComponent(type) : type;
        // Escape markdown special characters in link text: backslashes first, then brackets
        const escapedName = name.replace(/\\/g, '\\\\').replace(/\[/g, '\\[').replace(/\]/g, '\\]');
        const newText = text.replace(
            match,
            !!encodedType
                ? `[${escapedName}](/#${route}/${encodedId}/${encodedType})`
                : `[${escapedName}](/#${route}/${encodedId})`,
        );
        return newText;
    }, []);

    const parseChartEmbeds = useCallback((text) => {
        const id = getRegexMatch(text, CHART_REGEX);
        return id ? `[](#fm_report '{"id":"${id}"}')` : text;
    }, []);

    const parseTableEmbeds = useCallback((text) => {
        const id = getRegexMatch(text, TABLE_REGEX);
        return id ? `[](#fm_table '{"id":"${id}"}')` : text;
    }, []);

    const parseCopilotEmbeds = useCallback(
        (text) => {
            if (!text) return;

            let newText = text.replace(DETAIL_REGEX_GLOBAL, parseDetailLinks);
            newText = newText.replace(CHART_REGEX_GLOBAL, parseChartEmbeds);
            newText = newText.replace(TABLE_REGEX_GLOBAL, parseTableEmbeds);

            return newText;
        },
        [parseChartEmbeds, parseDetailLinks, parseTableEmbeds],
    );

    return {
        insertTextInDOM,
        cleanChatTextForClipboard,
        parseCopilotEmbeds,
    };
};

export default useBuildAnswer;
export { COPILOT_CHAT_STREAM_IDENTIFIERS };
